import React, { useState, useEffect } from "react";
import { Button, Tooltip } from "@mui/material";
import roundFilterList from "@iconify/icons-ic/round-filter-list";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../Hooks/AdminContext";
import StatusChip from "../../theme/chip";
import { useLocation, useParams } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import BasicBreadcrumbs from "../../components/GeneralComponents/BreadCrumbs";
import { Icon } from "@iconify/react/dist/iconify.js";
import CustomModal from "../../components/CustomModel";
import CustomTable from "../../components/customTable/CustomTable";
import AccountFilter from "./AgencyAdAccountFilter";
import CustomDrawer from "../../components/CustomDrawer";
import FilteredChip from "../../components/FilteredChip";
import {
  _Add_Ad_Account,
  _List_Ad_Account_For_Admin,
  _Update_Ad_Account_status,
  Scree_Base_List,
} from "../../DAL/Listing/AgencyAddAcount";
import moment from "moment";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import { show_proper_words } from "../../utils/constant";
import { AgencyAccountDetailsModal } from "./AgencyAccountDetailsModal";

const AgencyAdAccounts = ({ profile }) => {
  const param = useParams();
  const { setnavbarTitle, socket } = useAdminContext();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [rowDataID, setRowDataID] = useState("");
  const [rowData, setRowData] = useState({});
  const [employeestatus, setEmployeeStatus] = useState(null);
  const [modelState, setOpenModal] = useState(false);
  const [filterDrawerState, setFilterDrawerState] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employeeArray, setEmployeeArray] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [userList, setUserList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const EMPTY_FILTER = {
    account_type: null,
    raw_status: null,
  };
  const [filterData, setFilterData] = useState(EMPTY_FILTER);
  const [filterStateUpdated, setFilterStateUpdated] = useState(EMPTY_FILTER);
  const [Data, setData] = useState({
    address: "",
  });

  /**
   * Resets all relevant states and closes the modal.
   * Clears row data, employee info, and other modal-related states, then sets the modal's visibility to false.
   */
  const handleCloseModel = () => {
    setRowData(null);
    setRowDataID(null);
    setEmployee(null);
    setEmployeeStatus(null);
    setData({
      address: "",
    });
    setOpenModal(false);
  };

  /**
   * Updates the filter data state with the specified name and value.
   * Sets the filter data by merging the new value for the given name with the existing state.
   */
  const handleChangeOthers = (name, value) => {
    setFilterData((values) => ({ ...values, [name]: value }));
  };

  /**
   * Closes the filter drawer by setting its state to false.
   */
  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  /**
   * Clears the filter data, resets filter states, and reloads the ad account list.
   *
   * - Resets the filter data and filter state.
   * - Calls the API to fetch the ad account list with the default filters.
   * - Removes the stored filter data from local storage.
   * - Closes the filter drawer.
   */
  const handleClearFilter = () => {
    setFilterData(EMPTY_FILTER);
    setFilterStateUpdated(EMPTY_FILTER);
    GetAdsAccountList(0, rowsPerPage, searchText, EMPTY_FILTER);
    localStorage.removeItem("filterData_Agency_Account");
    handleCloseFilterDrawer();
  };

  /**
   * Removes specific filter values from the chip and updates the ad account list.
   *
   * - Sets missing properties (`raw_status`, `account_type`) to `null` in the filter data.
   * - Calls the API to fetch the ad account list with the updated filter data.
   * - Updates the filter data state and removes the stored filter from local storage.
   */
  const handleDeleteChip = (data) => {
    if (!data.raw_status) {
      data.raw_status = null;
    }
    if (!data.account_type) {
      data.account_type = null;
    }
    GetAdsAccountList(0, rowsPerPage, searchText, data);
    setFilterData(data);
    localStorage.removeItem("filterData_Agency_Account");
  };

  /**
   * Handles the search functionality and updates the ad account list.
   *
   * - Prevents the default form submission and resets the pagination.
   * - Calls the API to fetch the ad account list with the current search text and filter data.
   * - Saves the search text and filter data to local storage.
   * - Closes the filter drawer.
   */
  const searchFunction = (e) => {
    e.preventDefault();
    setPage(0);
    setPageCount(1);
    GetAdsAccountList(0, rowsPerPage, searchText, filterData);
    localStorage.setItem("searchText_Agency_Account", searchText);
    localStorage.setItem(
      "filterData_Agency_Account",
      JSON.stringify(filterData)
    );
    setFilterDrawerState(false);
  };

  // This function handles page changes for pagination.
  // It updates the page state to the new page number and adjusts the page count accordingly.
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  // This function handles changes in the number of rows per page for pagination.
  // It updates the rowsPerPage state to the new value and resets the page state to 0.
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // This function handles the page change for pagination.
  // If the new page is less than or equal to 0, it resets the page to 0 and sets the page count to 1.
  // Otherwise, it updates the page state to the new page and adjusts the page count accordingly.
  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  /**
   * Opens the filter drawer by setting its state to true.
   */
  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
  };

  /**
   * Fetches the list of ad accounts based on the given filters and search text.
   *
   * - Prepares the form data using search text and filters (account type, status).
   * - Sends the request to the API to fetch the ad account list with pagination.
   * - If the response is successful (code 200), it updates the UI with the ad account data, total count, and pages.
   * - Updates the filter state for UI updates and error handling if the fetch fails.
   * - Sets loading state during the API call.
   */
  const GetAdsAccountList = async (page, limit, searchText, filterData) => {
    setLoading(true);

    // Create PostData from filterData
    const PostData = { ...filterData };

    const formData = {
      search: searchText || "", // Ensuring empty search doesn't break API
      type: PostData.account_type || "", // Apply account type filter
      status: PostData.status_value || "", // Apply status filter
    };

    // console.log("Applied Filters:", formData);

    const response = await _List_Ad_Account_For_Admin(formData, page, limit);

    if (response.code === 200) {
      const {
        ad_account_list = [],
        total_pages = 0,
        total_count = 0,
      } = response;

      // Store filters for UI updates
      const ChipData = { ...filterData };

      if (ChipData?.raw_status && typeof ChipData.raw_status === "string") {
        ChipData.raw_status = {
          chip_label: show_proper_words(ChipData.raw_status),
          chip_value: show_proper_words(ChipData.raw_status),
        };
      }

      // console.log("Filtered Data State:", ChipData);

      // Update the filter state
      setFilterStateUpdated(ChipData);
      setUserList(ad_account_list);
      setTotalCount(total_count);
      setTotalPages(total_pages);
    } else {
      enqueueSnackbar(response.message || "Error fetching data", {
        variant: "error",
      });
    }

    setLoading(false);
  };

  /**
   * Handles the submission of the ad account status update form.
   *
   * - Determines the new status based on the employee's selected status.
   * - If the new status matches the current status, shows a warning and prevents submission.
   * - Prepares the form data and sends a request to update the ad account status.
   * - If the update is successful, emits a socket event, closes the modal, and reloads the ad account list.
   * - Displays success or error messages based on the response.
   * - Sets the loading state while the request is in progress.
   */
  const handleSubmit = async (e, data) => {
    e.preventDefault();
    let Status;
    if (employeestatus?.name === "Approved") {
      Status = "approved";
    } else if (employeestatus?.name === "Needs Changes") {
      Status = "need-changes";
    } else if (employeestatus?.name === "Review") {
      Status = "review";
    } else if (employeestatus?.name === "Rejected") {
      Status = "rejected";
    }

    if (Status === rowData.status) {
      enqueueSnackbar(
        "The selected status is the same as the current account status.",
        {
          variant: "warning",
        }
      );
      return;
    }
    setLoading(true);
    const formData = {
      business_center: employee || {},
      status: Status,
      message: Data?.address,
    };

    if (Status !== "approved") {
      delete formData.business_center;
    }

    const response = await _Update_Ad_Account_status(formData, rowDataID);
    if (response.code === 200) {
      socket.emit("ad_account_event", {
        notification: response.notification,
      });
      setOpenModal(false);
      enqueueSnackbar("Ad Account updated successfully", {
        variant: "success",
      });
      GetAdsAccountList(page, rowsPerPage, searchText, filterData);
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  /**
   * Fetches employer details based on the provided search value.
   *
   * - Sends a request to get business center data using the search value.
   * - If successful, updates the `employeeArray` state with the fetched list.
   */
  const GetEmployerDetails = async (value) => {
    const FormData = {
      type: "business-center",
      search: value,
    };
    const response = await Scree_Base_List(FormData);
    if (response.code === 200) {
      setEmployeeArray(response?.list);
    }
  };

  /**
   * Sets the row data and fetches employer details for the selected item.
   *
   * - Updates the `rowData` and `rowDataID` state with the selected data.
   * - Calls `GetEmployerDetails` to fetch details for the selected employer.
   */
  const handleShowDetails = (data) => {
    setRowData(data);
    setRowDataID(data._id);
    GetEmployerDetails();
  };

  /**
   * Defines the table header configuration for displaying user data.
   *
   * - Each object in the array represents a column in the table with properties such as `id`, `label`, and `renderData`.
   * - Includes columns for actions (e.g., viewing details), user information (name, email), account type, creation date, and status.
   * - The `renderData` function specifies how the data for each column is displayed, including event handlers for interactions (e.g., opening modals).
   * - Supports dynamic rendering of user-specific details and status information with tooltips for interactions.
   */
  const User_Table_Head = [
    {
      id: "action",
      label: "Action",
      renderData: (row) => (
        <>
          <Tooltip title="View Detail" placement="top">
            <RemoveRedEyeIcon
              onClick={() => {
                setData({
                  address: "",
                });
                setEmployeeStatus(null);
                handleShowDetails(row);
                setOpenModal(true);
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
          {/* {row.status === "approved" && (
            <Tooltip title="Manage Spendings" placement="top">
              <PriceChangeIcon
                onClick={() => {
                  navigate(`/manage-spendings/${row._id}`);
                }}
                style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
              />
            </Tooltip>
          )} */}
        </>
      ),
    },
    {
      id: "number",
      label: "#",
      type: "number",
    },
    !profile && {
      id: "user",
      label: "User",
      renderData: (row) => (
        <div
          className="pointer"
          onClick={() => {
            setRowData(row);
            setData({
              address: "",
            });
            handleShowDetails(row);
            setOpenModal(true);
          }}
        >
          <div>
            {show_proper_words(
              row?.user_info?.first_name + " " + row?.user_info?.last_name
            ) || "_ _"}
          </div>
          <div className="text-muted" style={{ fontSize: "0.9em" }}>
            {row?.user_info?.email || "_ _"}
          </div>
        </div>
      ),
    },
    {
      id: "accountName",
      label: "Account",
      renderData: (row) => (
        <div
          className="pointer"
          onClick={() => {
            setRowData(row);
            setData({
              address: "",
            });
            handleShowDetails(row);
            setOpenModal(true);
          }}
        >
          <div>{show_proper_words(row?.account_name) || "_ _"}</div>{" "}
          {/* Business Entity Above */}
          <div className="text-muted">{row?.email || "_ _"}</div>{" "}
          {/* Business Email Underneath */}
        </div>
      ),
    },

    {
      id: "platform",
      label: "Account Type",
      renderData: (row) => (
        <span
          className="pointer capitalized"
          onClick={() => {
            setData({
              address: "",
            });
            setRowData(row);
            handleShowDetails(row);
            setOpenModal(true);
          }}
        >
          {row?.type || "_ _"}
        </span>
      ),
    },
    {
      label: "Created At",
      renderData: (row) => (
        <span className="">
          {moment(row.createdAt).format("DD MMM YY hh:mm A") || "_ _"}
        </span>
      ),
    },
    {
      id: "state",
      label: "Status",
      renderData: (row) => (
        <div className="d-flex align-items-center">
          <StatusChip
            status={
              row.status === "approved"
                ? "Approved"
                : row.status === "need-changes"
                ? "Need Changes"
                : row.status === "review"
                ? "Review"
                : row.status === "rejected"
                ? "Rejected"
                : row.status === "disabled"
                ? "Disabled"
                : "Pending"
            }
            type="activeInactive"
          />
          {/* <div>
            {row.status !== "needs changes" ? (
              <Tooltip title="Needs Changes" placement="top">
                <RemoveRedEyeIcon
                  onClick={() => {
                    handleShowDetails(row);
                    setOpenModal3(true);
                  }}
                  style={{
                    fill: "#fff",
                    marginLeft: "10px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            ) : (
              ""
            )}
          </div> */}
        </div>
      ),
    },
  ];

  /**
   * Defines the breadcrumb navigation structure for user and account management.
   *
   * - Each object represents a breadcrumb with a `title`, `navigation` link, and status (`active` or `status`).
   * - The last breadcrumb represents the current page ("Agency Ad Accounts") and does not have a navigation link.
   * - Helps in displaying a breadcrumb trail for user navigation across the user and account management sections.
   */
  const BreadCrumbsList2 = [
    {
      title: "Use Management",
      navigation: `/user-management`,
      active: false,
    },
    {
      title: "User Profile",
      navigation: `/user-management/user-profile/${param?.id}`,
      active: false,
    },
    {
      title: `Agency Ad Accounts`,
      navigation: "",
      status: "Active",
    },
  ];

  /**
   * Runs on component mount and whenever pagination or refresh state changes.
   *
   * - Sets the navbar title based on the profile status.
   * - Retrieves and applies saved filter and search data from local storage to update the ad account list.
   * - If a filter is saved, it applies the filter and fetches the account list.
   * - If only a search text is saved, it sets the search text and fetches the account list.
   * - If neither is found, it resets the search text and fetches the account list with default parameters.
   */
  useEffect(() => {
    if (profile) {
      setnavbarTitle("Agency Ad Accounts");
    } else {
      setnavbarTitle("Agency Accounts");
    }
    const Filter = localStorage.getItem("filterData_Agency_Account");
    const Search = localStorage.getItem("searchText_Agency_Account");
    if (Filter) {
      setFilterData(JSON.parse(Filter));
      GetAdsAccountList(page, rowsPerPage, searchText, JSON.parse(Filter));
    } else if (Search) {
      setSearchText(Search);
      GetAdsAccountList(page, rowsPerPage, Search, filterData);
    } else {
      setSearchText("");
      GetAdsAccountList(page, rowsPerPage, searchText, filterData);
    }
  }, [page, rowsPerPage, location.state?.refresh]);

  if (loading) {
    return <CustomCircularProgress />;
  }
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 mb-3">
          {profile && <BasicBreadcrumbs items={BreadCrumbsList2} />}
        </div>
        <div className="col-12 col-md-9">
          <FilteredChip
            data={filterStateUpdated}
            tempState={filterData}
            EMPTY_FILTER={EMPTY_FILTER}
            onDeleteChip={handleDeleteChip}
            onClear={handleClearFilter}
          />
        </div>
        <div className="col-12 col-md-3 text-end mb-3">
          <Button variant="contained" onClick={handleOpenFilterDrawer}>
            Filters &nbsp;&nbsp; <Icon icon={roundFilterList} />
          </Button>
        </div>
        <CustomTable
          data={userList}
          TABLE_HEAD={User_Table_Head}
          MENU_OPTIONS={[]}
          custom_search={{
            searchText: searchText,
            setSearchText: setSearchText,
            handleSubmit: searchFunction,
          }}
          custom_pagination={{
            total_count: totalCount,
            rows_per_page: rowsPerPage,
            page: page,
            handleChangePage: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
          pageCount={pageCount}
          totalPages={totalPages}
          pagePagination={true}
          handleChangePages={handleChangePages}
          placeHoldertext={"Search by account"}
        />
      </div>

      <CustomModal
        crossButton={true}
        // CustomSpacing={true}
        open={modelState}
        handleClose={handleCloseModel}
        component={
          <AgencyAccountDetailsModal
            userList={userList}
            setUserList={setUserList}
            setRowData={setRowData}
            rowData={rowData}
            handleSubmit={handleSubmit}
            setEmployeeStatus={setEmployeeStatus}
            employeestatus={employeestatus}
            Data={Data}
            setEmployee={setEmployee}
            employee={employee}
            employeeArray={employeeArray}
            setData={setData}
          />
        }
      />
      <CustomDrawer
        isOpenDrawer={filterDrawerState}
        onOpenDrawer={handleOpenFilterDrawer}
        onCloseDrawer={() => setFilterDrawerState(false)}
        pageTitle="Filters"
        componentToPassDown={
          <AccountFilter
            filterData={filterData}
            handleClearFilter={handleClearFilter}
            handleChangeOthers={handleChangeOthers}
            searchSubmitFilter={searchFunction}
            setFilterData={setFilterData}
          />
        }
      />
    </div>
  );
};

export default AgencyAdAccounts;
